/**
 * Album registry — the single import point for everything content-driven.
 *
 * Adding an album:
 *   1. Create `src/data/albums/<id>/album.ts` exporting `library` (and `deepDive` if available).
 *   2. If available, create `src/data/albums/<id>/songs.ts` exporting `songs`.
 *   3. Register the imports below.
 *   4. Run `pnpm build`. Schema errors will print precise field paths.
 */

import {
  AlbumModuleSchema,
  formatAlbumError,
  type AlbumModule,
  type AvailableAlbumModule,
} from '@/types/album';
import { ZodError } from 'zod';

// === Available albums =====================================================
import { library as toxicityLibrary, deepDive as toxicityDeepDive } from './toxicity/album';
import { songs as toxicitySongs } from './toxicity/songs';

import {
  library as okComputerLibrary,
  deepDive as okComputerDeepDive,
} from './ok-computer/album';
import { songs as okComputerSongs } from './ok-computer/songs';

import { library as lateralusLibrary, deepDive as lateralusDeepDive } from './lateralus/album';
import { songs as lateralusSongs } from './lateralus/songs';

import {
  library as libadLibrary,
  deepDive as libadDeepDive,
} from './life-is-but-a-dream/album';
import { songs as libadSongs } from './life-is-but-a-dream/songs';

import {
  library as loveYourselfTearLibrary,
  deepDive as loveYourselfTearDeepDive,
} from './love-yourself-tear/album';
import { songs as loveYourselfTearSongs } from './love-yourself-tear/songs';

// === Coming soon ==========================================================
// Coming-soon slots stay disabled while we iterate on the picker UI.
// Uncomment any of these to bring back coming-soon slots:
// import { library as toPimpAButterflyLibrary } from './to-pimp-a-butterfly/album';
// import { library as masterOfPuppetsLibrary } from './master-of-puppets/album';
// import { library as rumoursLibrary } from './rumours/album';
// import { library as inTheAeroplaneLibrary } from './in-the-aeroplane/album';

// Validate at module-load time. A malformed module fails the build/dev start.
function validate(id: string, mod: unknown): AlbumModule {
  try {
    return AlbumModuleSchema.parse(mod);
  } catch (err) {
    if (err instanceof ZodError) {
      throw new Error(formatAlbumError(id, err), { cause: err });
    }
    throw err;
  }
}

const RAW_MODULES: Array<{ id: string; mod: unknown }> = [
  {
    id: 'toxicity',
    mod: { library: toxicityLibrary, deepDive: toxicityDeepDive, songs: toxicitySongs },
  },
  {
    id: 'love-yourself-tear',
    mod: {
      library: loveYourselfTearLibrary,
      deepDive: loveYourselfTearDeepDive,
      songs: loveYourselfTearSongs,
    },
  },
  {
    id: 'ok-computer',
    mod: { library: okComputerLibrary, deepDive: okComputerDeepDive, songs: okComputerSongs },
  },
  {
    id: 'lateralus',
    mod: { library: lateralusLibrary, deepDive: lateralusDeepDive, songs: lateralusSongs },
  },
  {
    id: 'life-is-but-a-dream',
    mod: { library: libadLibrary, deepDive: libadDeepDive, songs: libadSongs },
  },
  // { id: 'to-pimp-a-butterfly', mod: { library: toPimpAButterflyLibrary } },
  // { id: 'master-of-puppets', mod: { library: masterOfPuppetsLibrary } },
  // { id: 'rumours', mod: { library: rumoursLibrary } },
  // { id: 'in-the-aeroplane', mod: { library: inTheAeroplaneLibrary } },
];

export const ALBUMS: ReadonlyMap<string, AlbumModule> = new Map(
  RAW_MODULES.map(({ id, mod }) => [id, validate(id, mod)])
);

/** Display order for the picker grid + library count copy. */
export const ALBUM_ORDER: ReadonlyArray<string> = RAW_MODULES.map((m) => m.id);

/** All library entries, in display order. */
export const LIBRARY = ALBUM_ORDER.map((id) => {
  const mod = ALBUMS.get(id);
  if (!mod) throw new Error(`registry: missing album "${id}"`);
  return mod.library;
});

/** Available (analyzed) albums — keyed for hero/deep-dive/songs rebinding. */
export const AVAILABLE_ALBUMS: ReadonlyMap<string, AvailableAlbumModule> = new Map(
  Array.from(ALBUMS.entries()).filter(
    (entry): entry is [string, AvailableAlbumModule] => entry[1].library.available
  )
);

/** First available album id — used as the default initial state. */
export const DEFAULT_ALBUM_ID: string = (() => {
  const first = ALBUM_ORDER.find((id) => ALBUMS.get(id)?.library.available);
  if (!first) throw new Error('registry: no available albums');
  return first;
})();

/** Type guard for callers that need to narrow to an analyzed album. */
export function isAvailable(mod: AlbumModule): mod is AvailableAlbumModule {
  return mod.library.available;
}

/** Look up a module, throwing if unknown. */
export function getAlbum(id: string): AlbumModule {
  const mod = ALBUMS.get(id);
  if (!mod) throw new Error(`unknown album id: ${id}`);
  return mod;
}

/** Look up an analyzed album, throwing if unknown or coming-soon. */
export function getAvailableAlbum(id: string): AvailableAlbumModule {
  const mod = AVAILABLE_ALBUMS.get(id);
  if (!mod) throw new Error(`album "${id}" is unavailable or unknown`);
  return mod;
}

/** Library count — used by the picker header copy ("06 discos analizados"). */
export const ALBUM_COUNT = LIBRARY.length;
